import { useState } from 'react';
import PropTypes from 'prop-types';
import { ArrowDownTrayIcon } from '@heroicons/react/24/outline';

const PERMISSIONS_OPTIONS = [
  { value: 'project', label: 'Project' },
  { value: 'global', label: 'Global' },
];

const SaveConfigurationModal = ({
  show,
  onCancel,
  onSave,
  isLoading,
  mode = 'create',
  initialName = '',
  initialPermissions = 'project',
}) => {
  const [name, setName] = useState(initialName);
  const [permissions, setPermissions] = useState(initialPermissions);

  if (!show) {
    return null;
  }

  const permissionOptions = PERMISSIONS_OPTIONS.map(option => ({
    ...option,
    disabled: initialPermissions === 'global' && option.value === 'project',
  }));

  const isEditMode = mode === 'edit';

  const handleSave = () => {
    if (!name.trim()) return;
    onSave({ name: name.trim(), permissions });
  };

  const handleCancel = () => {
    onCancel();
  };

  return (
    <dialog open={show} className="modal">
      <div className="modal-box">
        <div className="flex items-center gap-3 mb-4">
          <div className="bg-blue-100 p-2 rounded-full">
            <ArrowDownTrayIcon className="w-6 h-6 text-blue-600" />
          </div>
          <h3 className="font-bold text-lg">{isEditMode ? 'Edit configuration' : 'Save configuration'}</h3>
        </div>
        <p className="text-gray-600 mb-6">
          {isEditMode
            ? "Update this configuration's name and sharing scope."
            : "Save this module's current parameter values as a named configuration. You can reuse it when adding this module to any pathway."}
        </p>
        <div className="mb-4">
          <label htmlFor="config-name" className="block text-sm font-medium mb-1">
            Configuration name
          </label>
          <input
            id="config-name"
            type="text"
            className="input input-bordered input-sm w-full"
            placeholder="e.g. My baseline"
            value={name}
            onChange={e => setName(e.target.value)}
            disabled={isLoading}
          />
        </div>
        <div className="mb-4">
          <label htmlFor="config-permissions" className="block text-sm font-medium mb-1">
            Permissions
          </label>
          <select
            id="config-permissions"
            className="select select-bordered select-sm w-full"
            value={permissions}
            onChange={e => setPermissions(e.target.value)}
            disabled={isLoading}
          >
            {permissionOptions.map(opt => (
              <option key={opt.value} value={opt.value} disabled={opt.disabled}>
                {opt.label}
              </option>
            ))}
          </select>
        </div>
        {initialPermissions === 'global' && (
          <p className="text-xs text-gray-500">Global configurations cannot be changed back to project scope.</p>
        )}
        <div className="modal-action">
          <button onClick={handleCancel} disabled={isLoading} className="btn btn-sm btn-outline">
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={isLoading || !name.trim()}
            className="btn btn-sm bg-blue-500 hover:bg-blue-600 text-white border-none"
          >
            {isLoading ? (
              <span className="loading loading-spinner loading-sm"></span>
            ) : isEditMode ? (
              'Save changes'
            ) : (
              'Save'
            )}
          </button>
        </div>
      </div>
      <div className="modal-backdrop bg-black/50" onClick={!isLoading ? handleCancel : undefined}></div>
    </dialog>
  );
};

SaveConfigurationModal.propTypes = {
  show: PropTypes.bool.isRequired,
  onCancel: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  isLoading: PropTypes.bool,
  mode: PropTypes.oneOf(['create', 'edit']),
  initialName: PropTypes.string,
  initialPermissions: PropTypes.oneOf(['project', 'global']),
};

export default SaveConfigurationModal;
